// VIBE-1 — THE ADVERTISING PIXEL'S LAW (2026-08-10, her order: TV campaigns are blocked until this
// fires). Shared, because the SERVER decides which purchases are real and the CLIENT is what calls
// the vendor, and "what may ride an event" must not be two answers (R28/R29). It imports NOTHING, so
// `scripts/vibe-pixel.mjs` can load and RUN every law in here on the FAST tier with no browser, no
// database and no network — the entry requirement every law in `src/*.js` is held to.
//
// WHAT THE VENDOR ACTUALLY ACCEPTS, MEASURED RATHER THAN ASSUMED (2026-08-10). `s.vibe.co/vbpx.js`
// was fetched and read: `process` dispatches `"event"` to `new Pixel("xhr", event, now, options, n)`
// where `n` is the THIRD argument, and the pixel serialises it into the request as `ed:` (event
// data) via a generic `serializeData` — `JSON.stringify` on any object, with NO client-side key
// validation at all. So a third argument IS supported and is passed through opaquely. Vibe's own
// manual-install docs describe `value` and `currency` for a purchase (and an optional purchase id),
// which is the shape used here. **Nothing about the key names is enforced by the vendor's script**,
// which is exactly why the roster below is ours and is held closed.

export const VIBE_PIXEL_ID = "cvk5gz";

// The two events this house sends. Named so a third one cannot be introduced by a call site typing a
// string — an unrostered event name is refused rather than sent (R17/R25: an unrostered key falling
// through to a plausible wrong answer is how a signal goes quietly missing).
export const PIXEL_EVENTS = ["page_view", "purchase"];
export const isPixelEvent = (name) => PIXEL_EVENTS.includes(String(name || ""));

// ---------------------------------------------------------------------------
// HER CLAUSE 3 — THE PRIVACY LAW, AS A BICONDITIONAL RATHER THAN A PROMISE.
//
// "The pixel receives page and event signals only; no member personal data, email, name or birth
// details rides any event call." A comment saying so is worth nothing: the failure has NO symptom on
// this site — the member sees the same screen, the owner sees the same dashboard, and the only party
// who learns her birthday is the ad vendor. So the roster is INVERTED, exactly as I3 inverted the
// scrollbar roster: **a key is refused unless it is named here**, and a payload carrying anything
// else is not sent at all.
//
// Failing CLOSED is the whole point. A roster of BANNED keys is green the day somebody adds
// `first_name`; a roster of ALLOWED keys goes red on the same edit. The two directions are not the
// same size (R19/R8-4): refusing a legitimate new key is loud and a human fixes it in a minute,
// while leaking one is silent and permanent.
// **HER VENDOR'S OWN SPEC IS THE AUTHORITY, AND IT CORRECTS THIS ROUND'S FIRST SHIPPING (2026-08-10).**
// The first build sent `{ value, currency }` on the strength of Vibe's manual-install prose. Vibe's
// ADDITIONAL EVENTS spec — her screenshot — states the purchase event as
// `vbpx('event','purchase', {'price_usd': AMOUNT})` with an optional `purchase_id`. `vbpx.js` does no
// key validation at all (measured: the third argument is JSON-serialised into `ed` untouched), so the
// wrong key names left the account without a recognised price on every event: from Vibe's side all
// six fired BARE. **A vendor's own field names are a fact to be read, never inferred from prose** —
// this is R41's law (identity is the id, never a spelling) arriving on an integration.
export const PIXEL_SAFE_KEYS = ["price_usd", "purchase_id"];

// The shapes personal data actually arrives in on this site, named so the refusal can SAY which rule
// it broke rather than answering a bare false. These are checked on the KEY and on the VALUE: a
// caller passing `{ value: "lala@example.com" }` uses a rostered key and is still a leak.
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const DATE_RE = /\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/;

export function payloadPrivacyViolations(payload) {
  const out = [];
  if (payload === null || payload === undefined) return out;   // a bare event is always safe
  if (typeof payload !== "object" || Array.isArray(payload)) return ["payload is not a plain object"];
  for (const [key, value] of Object.entries(payload)) {
    if (!PIXEL_SAFE_KEYS.includes(key)) { out.push(`unrostered key \`${key}\``); continue; }
    // A rostered key may still carry the wrong thing.
    if (typeof value === "object" && value !== null) { out.push(`\`${key}\` carries an object`); continue; }
    const text = String(value);
    if (EMAIL_RE.test(text)) out.push(`\`${key}\` carries an email address`);
    else if (DATE_RE.test(text)) out.push(`\`${key}\` carries a date`);
  }
  return out;
}

export const payloadIsPrivacySafe = (payload) => payloadPrivacyViolations(payload).length === 0;

// ---------------------------------------------------------------------------
// HER CLAUSE 2 — WHO NEVER FIRES A PURCHASE.
//
// "Test-flagged accounts and owner seats never fire purchase events." Both are asked `=== true`
// because these flags arrive off JSON and `Boolean("false")` is `true` (R136/R137, paid here before).
//
// AND IT FAILS CLOSED ON AN UNKNOWN SEAT, WHICH IS THE OPPOSITE OF ITEM 12's RING AND DELIBERATELY
// SO. There, an unreadable seat is REFUSED because an unknown might be a real member who would be
// rung. Here the consequence is reversed: firing wrongly puts a fake conversion into the campaign
// the owner is buying TV against, and a campaign optimising on the house's own test checkouts is
// worse than one missing a sale. So `known: false` never fires.
export function pixelPurchaseEligible(seat) {
  const s = seat || {};
  if (s.known !== true) return false;
  if (s.isHarness === true) return false;
  if (s.isOwner === true) return false;
  return true;
}

// ---------------------------------------------------------------------------
// THE PURCHASE PAYLOAD.
//
// `value` is DOLLARS, not cents — Vibe's docs show `value: 123.45` beside `currency: 'USD'`, and a
// pixel handed 14900 would report a $149 sale as a $14,900 one and wreck the ROAS the campaign is
// optimised on. The conversion is done HERE, once, rather than at the call site.
//
// AN UNKNOWN AMOUNT FIRES THE BARE EVENT, WHICH IS HER OWN FALLBACK RULE ("if unverifiable, fire the
// bare event"). It matters for the portal upgrade path: `customer.subscription.updated` carries no
// amount charged, so inventing one — the new tier's sticker price, say — would report $149 for what
// may have been an $80 prorated step. A missing value is a gap in the reporting; a WRONG value is a
// number the owner will make spending decisions on.
export function purchasePayload({ amountCents = null, purchaseId = "" } = {}) {
  const out = {};
  const cents = Number(amountCents);
  // **DOLLARS, NEVER CENTS.** A $149.00 charge sends 149, not 14900 — the difference between a real
  // ROAS and one that is a hundred times too good, in the direction nobody questions.
  if (amountCents !== null && amountCents !== undefined && Number.isFinite(cents) && cents > 0) {
    out.price_usd = Math.round(cents) / 100;
  }
  // THEIR DEDUPE KEY AS WELL AS OURS. Ours is the UNIQUE INDEX on `stripe_ref` plus claim-before-fire,
  // which stops us sending twice; `purchase_id` lets Vibe collapse anything that reaches them twice
  // anyway — a re-fire, a retry, a member with two tabs. Two independent dedupes for one sale is
  // belt and braces on the one number her TV spend is optimised against.
  const id = String(purchaseId || "").trim();
  if (id) out.purchase_id = id;
  // A bare event only when there is genuinely nothing to say (her own fallback rule).
  return Object.keys(out).length ? out : null;
}

// ---------------------------------------------------------------------------
// THE ONE DOOR. Every pixel call on this site goes through here, so the privacy law cannot be
// walked around by a call site that reaches `window.vbpx` itself — and the fixture holds that there
// is no second caller.
//
// `win` is a PARAMETER rather than a reach for the global, so a gate RUNS this against a fake window
// and reads back exactly what was sent (locked-door.ts's own repair: pure side effects on `window`
// meant a live Chrome was the only instrument that could reach it).
export function sendPixelEvent(win, name, payload = null) {
  if (!win || typeof win.vbpx !== "function") return { sent: false, reason: "pixel_absent" };
  if (!isPixelEvent(name)) return { sent: false, reason: "unrostered_event" };
  const bad = payloadPrivacyViolations(payload);
  if (bad.length) return { sent: false, reason: "privacy_refused", violations: bad };
  if (payload === null || payload === undefined) { win.vbpx("event", name); return { sent: true, name, payload: null }; }
  win.vbpx("event", name, payload);
  return { sent: true, name, payload };
}
